import React from 'react';
import PropTypes from 'prop-types';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';

const normalize = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');

const itemSubjects = (item) => item.Subject || item.subjects || [];

// Sem data de publicação (Arquivo e Imagem não têm estado e nunca a recebem), o
// catálogo devolve 1969-12-31: o item sai sem data, como no AgsusListaTemplate.
export const formatDate = (value) => {
  if (!value || value.startsWith('1969') || value.startsWith('1970-01-01'))
    return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
};

const statusLabel = (item) => {
  const text = normalize(`${item.title || ''} ${itemSubjects(item).join(' ')}`);
  if (text.includes('cancelad')) return 'Cancelada';
  if (text.includes('encerrad')) return 'Encerrada';
  if (text.includes('abert') || text.includes('andamento')) return 'Aberta';
  return null;
};

const AgsusFilteredListing = ({ items, isEditMode, data = {} }) => {
  const [term, setTerm] = React.useState('');
  const [status, setStatus] = React.useState('');
  const statuses = React.useMemo(
    () =>
      [...new Set(items.flatMap(itemSubjects).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'pt-BR'),
      ),
    [items],
  );
  const visibleItems = React.useMemo(() => {
    const needle = normalize(term.trim());
    return items.filter(
      (item) =>
        (!needle ||
          normalize(`${item.title || ''} ${item.description || ''}`).includes(
            needle,
          )) &&
        (!status || itemSubjects(item).includes(status)),
    );
  }, [items, status, term]);

  return (
    <section className="ag-filtered-listing">
      <div className="ag-filtered-listing__filters">
        <label className="ag-filtered-listing__search">
          <span>Pesquisar nesta lista</span>
          <input
            type="search"
            value={term}
            placeholder="Digite um termo para pesquisar"
            onChange={(event) => setTerm(event.target.value)}
          />
        </label>
        {statuses.length > 1 && (
          <label>
            <span>Status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">Todos</option>
              {statuses.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {data.headline && <h2>{data.headline}</h2>}
      <ul className="ag-lista__list">
        {visibleItems.map((item) => (
          <li key={item['@id']} className="ag-lista__item">
            <ConditionalLink item={item} condition={!isEditMode}>
              <span className="ag-lista__item-main">
                <strong className="ag-lista__item-title">
                  {item.title || item.id}
                </strong>
                {item.description && (
                  <small className="ag-lista__item-description">
                    {item.description}
                  </small>
                )}
              </span>
              {(formatDate(item.effective) || statusLabel(item)) && (
                <span className="ag-lista__item-meta">
                  {formatDate(item.effective) && (
                    <span className="ag-lista__item-date">
                      {formatDate(item.effective)}
                    </span>
                  )}
                  {statusLabel(item) && (
                    <mark className="ag-lista__item-status">
                      {statusLabel(item)}
                    </mark>
                  )}
                </span>
              )}
            </ConditionalLink>
          </li>
        ))}
      </ul>
      {!visibleItems.length && (
        <p className="ag-filtered-listing__empty">
          Nenhum item encontrado para os filtros selecionados.
        </p>
      )}
      <p className="ag-filtered-listing__count" aria-live="polite">
        {visibleItems.length} {visibleItems.length === 1 ? 'item' : 'itens'}
      </p>
    </section>
  );
};

AgsusFilteredListing.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  isEditMode: PropTypes.bool,
  data: PropTypes.object,
};

export default AgsusFilteredListing;
